import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Pencil,
  KeyRound,
  ShieldCheck,
  Check,
} from "lucide-react";

const ROLES = [
  { v: "admin", l: "Admin" },
  { v: "mali_musavir", l: "Mali Müşavir" },
  { v: "ofis_yoneticisi", l: "Ofis Yöneticisi" },
  { v: "kidemli", l: "Kıdemli Personel" },
  { v: "muhasebe", l: "Muhasebe Personeli" },
  { v: "bordro", l: "Bordro Personeli" },
  { v: "stajyer", l: "Stajyer" },
  { v: "salt_okuma", l: "Salt Okuma" },
];

const PERMISSIONS = [
  { v: "dashboard", l: "Dashboard" },
  { v: "mukellefler", l: "Mükellefler" },
  { v: "beyannameler", l: "Beyannameler" },
  { v: "edefter", l: "e-Defter" },
  { v: "gib", l: "GİB Entegrasyon" },
  { v: "etebligat", l: "e-Tebligat" },
  { v: "gorevler", l: "Görevler" },
  { v: "cari", l: "Cari Hesap" },
  { v: "tahsilatlar", l: "Tahsilatlar" },
  { v: "takvim", l: "Takvim" },
  { v: "raporlar", l: "Raporlar" },
  { v: "araclar", l: "Araçlar" },
  { v: "bildirimler", l: "Bildirimler" },
  { v: "asistan", l: "Akıllı Asistan" },
  { v: "personel", l: "Personel" },
  { v: "audit", l: "İşlem Kayıtları" },
  { v: "ayarlar", l: "Ayarlar" },
];

const roleLabel = (v) =>
  ROLES.find((r) => r.v === v)?.l || v;

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "muhasebe",
  permissions: [],
};

export default function Personel() {
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const canManage = ["admin", "mali_musavir"].includes(user?.role);

  const load = async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch {
      toast.error("Personel listesi alınamadı");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      permissions: PERMISSIONS.map((p) => p.v),
    });
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);

    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "muhasebe",
      permissions: Array.isArray(u.permissions)
        ? u.permissions
        : [],
    });

    setOpen(true);
  };

  const togglePermission = (permission) => {
    setForm((prev) => {
      const current = Array.isArray(prev.permissions)
        ? prev.permissions
        : [];

      if (current.includes(permission)) {
        return {
          ...prev,
          permissions: current.filter((p) => p !== permission),
        };
      }

      return {
        ...prev,
        permissions: [...current, permission],
      };
    });
  };

  const selectAll = () => {
    setForm((prev) => ({
      ...prev,
      permissions: PERMISSIONS.map((p) => p.v),
    }));
  };

  const clearAll = () => {
    setForm((prev) => ({
      ...prev,
      permissions: [],
    }));
  };

  const save = async () => {
    if (!form.name || !form.email) {
      toast.error("Ad Soyad ve e-posta zorunludur");
      return;
    }

    if (!editing && !form.password) {
      toast.error("Yeni personel için şifre zorunludur");
      return;
    }

    setSaving(true);

    try {
      if (editing) {
        const payload = {
          name: form.name,
          email: form.email,
          role: form.role,
          permissions: form.permissions,
        };

        if (form.password) {
          payload.password = form.password;
        }

        await api.put(`/users/${editing.id}`, payload);

        toast.success("Personel bilgileri güncellendi");
      } else {
        await api.post("/users", {
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
          permissions: form.permissions,
        });

        toast.success("Personel eklendi");
      }

      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      await load();
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "İşlem gerçekleştirilemedi"
      );
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Bu personeli silmek istediğinize emin misiniz?")) {
      return;
    }

    try {
      await api.delete(`/users/${id}`);
      toast.success("Personel silindi");
      await load();
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Personel silinemedi"
      );
    }
  };

  return (
    <div className="space-y-5 fade-in">

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-semibold tracking-tight">
            Personel
          </h1>

          <p className="text-sm text-muted-foreground mt-1">
            Ofis kullanıcıları, roller ve bölüm yetkileri
          </p>
        </div>

        {canManage && (
          <Button
            data-testid="add-user-btn"
            onClick={openNew}
          >
            <Plus size={16} className="mr-1.5" />
            Yeni Personel
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

        {users.map((u) => (
          <Card
            key={u.id}
            className="p-5 flex items-center gap-3"
          >

            <Avatar
              user={u}
              className="w-11 h-11 rounded-full bg-accent grid place-items-center font-semibold"
            />

            <div className="flex-1 min-w-0">

              <div className="font-medium truncate">
                {u.name}
              </div>

              <div className="text-xs text-muted-foreground truncate">
                {u.email}
              </div>

              <div className="flex items-center gap-2 mt-1">
                <Badge variant="secondary">
                  {roleLabel(u.role)}
                </Badge>

                {u.role !== "admin" && (
                  <span className="text-[10px] text-muted-foreground">
                    {Array.isArray(u.permissions)
                      ? `${u.permissions.length} bölüm`
                      : "Yetki ayarlanmamış"}
                  </span>
                )}
              </div>

            </div>

            {canManage && (
              <div className="flex items-center gap-1">

                <button
                  onClick={() => openEdit(u)}
                  title="Düzenle"
                  className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                >
                  <Pencil size={16} />
                </button>

                {u.role !== "admin" && (
                  <button
                    onClick={() => del(u.id)}
                    title="Sil"
                    className="p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-rose-500"
                  >
                    <Trash2 size={16} />
                  </button>
                )}

              </div>
            )}

          </Card>
        ))}

      </div>

      <Dialog
        open={open}
        onOpenChange={setOpen}
      >

        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">

          <DialogHeader>

            <DialogTitle>
              {editing
                ? "Personel Düzenle"
                : "Yeni Personel"}
            </DialogTitle>

          </DialogHeader>

          <div className="space-y-5 py-2">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <div className="space-y-1.5">
                <Label>Ad Soyad</Label>

                <Input
                  value={form.name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      name: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-1.5">
                <Label>E-posta</Label>

                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      email: e.target.value,
                    })
                  }
                />
              </div>

            </div>

            <div className="space-y-1.5">

              <Label>
                {editing
                  ? "Yeni Şifre (değiştirmek istemiyorsanız boş bırakın)"
                  : "Şifre"}
              </Label>

              <div className="relative">

                <KeyRound
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />

                <Input
                  className="pl-9"
                  type="password"
                  value={form.password}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      password: e.target.value,
                    })
                  }
                  placeholder={
                    editing
                      ? "Yeni şifre"
                      : "Şifre"
                  }
                />

              </div>

            </div>

            <div className="space-y-1.5">

              <Label>Rol</Label>

              <Select
                value={form.role}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    role: v,
                  })
                }
              >

                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem
                      key={r.v}
                      value={r.v}
                    >
                      {r.l}
                    </SelectItem>
                  ))}
                </SelectContent>

              </Select>

            </div>

            <div className="border rounded-xl p-4">

              <div className="flex items-center justify-between mb-3">

                <div>
                  <div className="font-medium flex items-center gap-2">
                    <ShieldCheck size={18} />
                    Görülebilecek Bölümler
                  </div>

                  <div className="text-xs text-muted-foreground mt-1">
                    Bu personelin sol menüde görebileceği bölümleri seçin.
                  </div>
                </div>

                <div className="flex gap-2">

                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={selectAll}
                  >
                    Tümünü Seç
                  </Button>

                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={clearAll}
                  >
                    Temizle
                  </Button>

                </div>

              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">

                {PERMISSIONS.map((p) => {

                  const checked =
                    form.permissions?.includes(p.v);

                  return (
                    <button
                      key={p.v}
                      type="button"
                      onClick={() =>
                        togglePermission(p.v)
                      }
                      className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                        checked
                          ? "bg-primary/10 border-primary"
                          : "hover:bg-accent"
                      }`}
                    >

                      <div
                        className={`w-5 h-5 rounded border grid place-items-center shrink-0 ${
                          checked
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border"
                        }`}
                      >

                        {checked && (
                          <Check size={13} />
                        )}

                      </div>

                      <span className="text-sm">
                        {p.l}
                      </span>

                    </button>
                  );

                })}

              </div>

            </div>

          </div>

          <DialogFooter>

            <Button
              variant="outline"
              onClick={() => setOpen(false)}
            >
              İptal
            </Button>

            <Button
              onClick={save}
              disabled={saving}
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>

          </DialogFooter>

        </DialogContent>

      </Dialog>

    </div>
  );
}
